import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { AlertCircle, CheckCircle2, ExternalLink, Loader2, TriangleAlert, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { adminBillingService } from '../services/admin.billing.service'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import type { ClientEngagementDocument } from '@/types'
import { useEngagementInvoicing } from '../hooks/useEngagementInvoicing'
import { getEstimateEmailDisplay, timestampToMillis } from '../lib/estimateEmailStatus'
import { reconcileStripeEngagementFn } from '@/lib/firebase/functions'

interface Props {
  engagement: ClientEngagementDocument & { id: string }
  onRefresh: () => void
}

function StatusRow({
  label,
  value,
}: {
  label: string
  value: ReactNode
}) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  )
}

function fmt(ts: unknown): string {
  const milliseconds = timestampToMillis(ts)
  if (milliseconds === null) return '—'
  return new Date(milliseconds).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

export function EngagementInvoicePanel({ engagement, onRefresh }: Props) {
  const {
    loading, loadingAction, feedback, dismissFeedback,
    createEstimate, sendEstimateToClient,
    sendDepositInvoice, sendFinalInvoice,
  } = useEngagementInvoicing(engagement.id, onRefresh)

  const [depositThreshold, setDepositThreshold] = useState<number | null>(null)
  const [autoCancelDays, setAutoCancelDays] = useState(10)
  const [providerBufferHours, setProviderBufferHours] = useState(48)
  const [reconciling, setReconciling] = useState(false)
  const [reconcileMessage, setReconcileMessage] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    adminBillingService
      .getBillingSettings()
      .then((s) => {
        if (!cancelled) {
          setDepositThreshold(s.depositThreshold)
          setAutoCancelDays(s.depositAutoCancelDays)
          setProviderBufferHours(s.depositProviderBufferHours)
        }
      })
      .catch(() => { /* fallback to null; UI degrades to 'Not sent' */ })
    return () => { cancelled = true }
  }, [])

  const isDelivered = engagement.status === 'delivered'
  const hasDepositInvoice = !!engagement.stripeDepositInvoiceId
  const hasFinalInvoice = !!engagement.stripeFinalInvoiceId
  const estimatePrepared = !!engagement.estimatePreparedAt
  const isUnderThreshold =
    engagement.depositRequired !== undefined
      ? !engagement.depositRequired
      : depositThreshold !== null && (engagement.estimateAmount ?? 0) <= depositThreshold
  const isOverThreshold =
    engagement.depositRequired !== undefined
      ? engagement.depositRequired
      : depositThreshold !== null && (engagement.estimateAmount ?? 0) > depositThreshold
  const hasBeenSent = !!engagement.estimateSentAt
  const hasBeenAccepted = !!engagement.acceptedAt
  const estimateEmailDisplay = getEstimateEmailDisplay(engagement)
  const expiresAtMs = timestampToMillis(engagement.acceptanceTokenExpiresAt)
  const expiryDisplay = expiresAtMs
    ? new Date(expiresAtMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null

  // Deposit-unpaid reminder schedule visualization.
  // Reminders are active iff invoice sent + not paid + at least one task still
  // registered on the engagement. Auto-cancel date is computed from the same
  // adaptive formula the scheduler uses: min(invoiceSent + configured ceiling,
  // scheduled date minus the configured provider buffer).
  const depositRemindersActive =
    !!engagement.depositInvoiceSentAt &&
    !engagement.depositPaidAt &&
    (!!engagement.depositReminderTaskName ||
      !!engagement.depositFinalReminderTaskName ||
      !!engagement.depositAutoCancelTaskName)

  const depositAutoCancelDate = (() => {
    if (!depositRemindersActive || !engagement.depositInvoiceSentAt) return null
    const invoiceSentMs = timestampToMillis(engagement.depositInvoiceSentAt)
    if (invoiceSentMs === null) return null
    const ceilingMs = invoiceSentMs + autoCancelDays * 24 * 60 * 60 * 1000
    let cancelMs = ceilingMs
    if (engagement.scheduledDate) {
      const [Y, M, D] = engagement.scheduledDate.split('-').map(Number)
      if (Y && M && D) {
        const scheduledMs = Date.UTC(Y, M - 1, D, 0, 0, 0)
        const minusBuffer = scheduledMs - providerBufferHours * 60 * 60 * 1000
        if (minusBuffer < cancelMs) cancelMs = minusBuffer
      }
    }
    return new Date(cancelMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  })()

  const invoiceLink = (url: string, label: string) => (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary hover:underline"
    >
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  )

  const billingStateLabel: Record<string, string> = {
    deposit_due: 'Deposit due',
    deposit_paid: 'Deposit paid',
    final_due: 'Final invoice due',
    paid: 'Paid in full',
    cancelled: 'Cancelled',
  }

  const nextStep = useMemo(() => {
    if (engagement.status === 'cancelled') return 'No action — this engagement is cancelled.'
    if (engagement.finalPaidAt) return 'Complete — the engagement is paid in full.'
    if (hasFinalInvoice) return 'Waiting for the client to pay the final Stripe invoice.'
    if (isDelivered) return 'Review the saved line items, then send the final invoice.'
    if (engagement.finalInvoiceReviewReadyAt) {
      return 'The service date has passed. Review the work, then mark it Delivered to unlock final billing.'
    }
    if (hasDepositInvoice && !engagement.depositPaidAt) return 'Waiting for the client to pay the deposit.'
    if (hasBeenAccepted && isOverThreshold && !hasDepositInvoice) {
      return 'The estimate is accepted, but the deposit invoice needs to be sent.'
    }
    if (hasBeenAccepted) return 'Booking accepted. The final invoice is due after the service.'
    if (hasBeenSent) return 'Waiting for the client to accept the estimate.'
    if (estimatePrepared) return 'Review the estimate, then send it to the client.'
    return 'Prepare the estimate after confirming the scope, schedule, and pricing.'
  }, [
    engagement.status,
    engagement.finalPaidAt,
    engagement.depositPaidAt,
    engagement.finalInvoiceReviewReadyAt,
    hasFinalInvoice,
    isDelivered,
    hasDepositInvoice,
    hasBeenAccepted,
    hasBeenSent,
    estimatePrepared,
    isOverThreshold,
  ])

  const reconcileStripe = async () => {
    setReconciling(true)
    setReconcileMessage(null)
    try {
      const { data } = await reconcileStripeEngagementFn({ engagementId: engagement.id })
      setReconcileMessage(
        data.healthy
          ? 'Stripe reconciliation is healthy.'
          : withErrorCode(
              'Reconciliation found an exception. Review the invoice IDs and Stripe Dashboard.',
              'billing/reconciliation-exception',
            ),
      )
      onRefresh()
    } catch (err) {
      setReconcileMessage(formatUserFacingError(err, 'Stripe reconciliation failed.'))
    } finally {
      setReconciling(false)
    }
  }

  return (
    <div className="space-y-3">
      <Separator />
      <div>
        <p className="text-sm font-semibold mb-2">Invoicing</p>

        <div className="mb-3 rounded-md border border-primary/20 bg-primary/5 px-3 py-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Next step</p>
          <p className="mt-1 text-sm">{nextStep}</p>
        </div>

        {feedback && (
          <Alert
            variant={feedback.kind === 'error' ? 'destructive' : 'default'}
            className={feedback.kind === 'success'
              ? 'mb-3 border-emerald-500/40 bg-emerald-500/10 text-emerald-950 dark:text-emerald-100'
              : feedback.kind === 'warning'
                ? 'mb-3 border-amber-500/40 bg-amber-500/10 text-amber-950 dark:text-amber-100'
                : 'mb-3'}
          >
            {feedback.kind === 'success' ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : feedback.kind === 'warning' ? (
              <TriangleAlert className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertTitle className="pr-8">{feedback.title}</AlertTitle>
            <AlertDescription className="pr-8">
              <p>{feedback.message}</p>
              {feedback.detail && <p className="font-mono text-xs break-all">{feedback.detail}</p>}
            </AlertDescription>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1 h-7 w-7"
              aria-label="Dismiss billing message"
              onClick={dismissFeedback}
            >
              <X className="h-4 w-4" />
            </Button>
          </Alert>
        )}

        {!feedback && (estimateEmailDisplay.state === 'failed' || estimateEmailDisplay.state === 'stalled') && (
          <Alert variant="destructive" className="mb-3">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{estimateEmailDisplay.label}</AlertTitle>
            <AlertDescription>
              {estimateEmailDisplay.description}
              {engagement.estimateEmailFailureCode && (
                <span className="font-mono text-xs">Error code: {engagement.estimateEmailFailureCode}</span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {!feedback && estimateEmailDisplay.state === 'sending' && (
          <Alert className="mb-3 border-blue-500/40 bg-blue-500/10">
            <Loader2 className="h-4 w-4 animate-spin" />
            <AlertTitle>Sending estimate email</AlertTitle>
            <AlertDescription>{estimateEmailDisplay.description}</AlertDescription>
          </Alert>
        )}

        {!feedback && engagement.estimateLifecycleStatus === 'failed' && hasBeenSent && (
          <Alert className="mb-3 border-amber-500/40 bg-amber-500/10 text-amber-950 dark:text-amber-100">
            <TriangleAlert className="h-4 w-4" />
            <AlertTitle>Estimate sent; follow-up automation failed</AlertTitle>
            <AlertDescription>
              The email was accepted for delivery, but its reminder or expiration task was not scheduled. Resend the estimate or review the function logs.
            </AlertDescription>
          </Alert>
        )}

        {/* Status rows */}
        <div className="rounded-md border px-3 py-2 space-y-0.5 bg-muted/30 mb-3">
          <StatusRow
            label="Billing owner"
            value="Stripe"
          />
          {engagement.billingState && (
            <StatusRow
              label="Billing state"
              value={billingStateLabel[engagement.billingState] ?? engagement.billingState.replaceAll('_', ' ')}
            />
          )}
          <StatusRow
            label="Estimate"
            value={estimatePrepared ? 'Prepared' : 'Not prepared'}
          />
          {estimatePrepared && (
            <StatusRow
              label="Estimate email"
              value={
                hasBeenAccepted
                  ? `Client accepted ${fmt(engagement.acceptedAt)}`
                  : (
                      <span className={estimateEmailDisplay.state === 'sent'
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : estimateEmailDisplay.state === 'failed' || estimateEmailDisplay.state === 'stalled'
                          ? 'text-destructive'
                          : ''}>
                        {estimateEmailDisplay.label}
                      </span>
                    )
              }
            />
          )}
          {hasBeenSent && (
            <StatusRow
              label="Sent / expires"
              value={`${fmt(engagement.estimateSentAt)} · ${expiryDisplay ?? '—'}`}
            />
          )}
          {(engagement.estimateEmailRecipient || hasBeenSent) && (
            <StatusRow
              label="Email recipient"
              value={engagement.estimateEmailRecipient ?? engagement.clientEmail}
            />
          )}
          {engagement.estimateEmailMessageId && (
            <StatusRow
              label="Provider message ID"
              value={<span className="font-mono text-xs break-all">{engagement.estimateEmailMessageId}</span>}
            />
          )}
          {estimatePrepared && (
            <StatusRow
              label="Deposit Invoice"
              value={
                engagement.depositInvoiceSentAt
                  ? `Stripe · Sent ${fmt(engagement.depositInvoiceSentAt)}`
                  : isUnderThreshold
                    ? 'Not required (single-invoice flow)'
                    : 'Not sent'
              }
            />
          )}
          {engagement.depositInvoiceSentAt && (
            <StatusRow
              label="Deposit Payment"
              value={
                engagement.depositPaidAt
                  ? `Paid ${fmt(engagement.depositPaidAt)}`
                  : 'Awaiting payment'
              }
            />
          )}
          {engagement.stripeDepositInvoiceId && (
            <StatusRow label="Stripe Deposit Invoice ID" value={engagement.stripeDepositInvoiceId} />
          )}
          {engagement.stripeDepositHostedInvoiceUrl && (
            <StatusRow
              label="Deposit invoice"
              value={invoiceLink(engagement.stripeDepositHostedInvoiceUrl, 'Open in Stripe')}
            />
          )}
          {depositRemindersActive && depositAutoCancelDate && (
            <StatusRow
              label="Auto-cancel if unpaid"
              value={`${depositAutoCancelDate} (reminders scheduled)`}
            />
          )}
          {hasBeenAccepted && (
            <StatusRow
              label="Booking Confirmation"
              value={
                engagement.bookingConfirmationSentAt
                  ? `Sent ${fmt(engagement.bookingConfirmationSentAt)}`
                  : isOverThreshold && !engagement.depositPaidAt
                    ? 'Waiting for deposit payment'
                    : 'Sending…'
              }
            />
          )}
          {(isDelivered || engagement.finalInvoiceSentAt || engagement.finalPaidAt) && (
            <StatusRow
              label="Final Invoice"
              value={
                engagement.finalPaidAt
                  ? `Paid ${fmt(engagement.finalPaidAt)}`
                  : engagement.finalInvoiceSentAt
                    ? `Stripe · Sent ${fmt(engagement.finalInvoiceSentAt)}${engagement.finalInvoiceAmount ? ` · $${engagement.finalInvoiceAmount.toFixed(2)}` : ''}`
                    : 'Not sent'
              }
            />
          )}
          {engagement.finalInvoiceReviewReadyAt && !hasFinalInvoice && (
            <StatusRow
              label="Final invoice review"
              value={`Ready since ${fmt(engagement.finalInvoiceReviewReadyAt)}`}
            />
          )}
          {engagement.stripeFinalInvoiceId && (
            <StatusRow label="Stripe Final Invoice ID" value={engagement.stripeFinalInvoiceId} />
          )}
          {engagement.stripeFinalHostedInvoiceUrl && (
            <StatusRow
              label="Final invoice"
              value={invoiceLink(engagement.stripeFinalHostedInvoiceUrl, 'Open in Stripe')}
            />
          )}
          {(hasDepositInvoice || hasFinalInvoice) && (
            <div className="pt-2">
              <Button type="button" variant="ghost" size="sm" disabled={reconciling} onClick={() => void reconcileStripe()}>
                {reconciling ? 'Reconciling Stripe…' : 'Reconcile Stripe billing'}
              </Button>
              {reconcileMessage && <p className="mt-1 text-xs text-muted-foreground">{reconcileMessage}</p>}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="space-y-2">
          {/* Step 1: Create estimate */}
          {!estimatePrepared && !hasBeenAccepted && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              disabled={loading}
              onClick={createEstimate}
            >
              {loadingAction === 'prepare_estimate' ? 'Preparing…' : 'Prepare Estimate'}
            </Button>
          )}

          {/* Step 2: Send estimate to client for review/acceptance */}
          {estimatePrepared && !hasBeenAccepted && (
            <Button
              type="button"
              size="sm"
              className="w-full"
              disabled={loading}
              onClick={sendEstimateToClient}
            >
              {loadingAction === 'send_estimate'
                ? 'Sending…'
                : hasBeenSent
                  ? 'Resend Estimate to Client'
                  : 'Send Estimate to Client'}
            </Button>
          )}

          {/* Step 2 (manual override): Send deposit invoice.
              Normally the deposit invoice is auto-sent the moment the client
              accepts the estimate; this button is for edge cases (e.g. client
              accepted verbally, deposit is over threshold, admin wants to bill
              before the link is clicked). Wrapped in a confirmation modal so
              it's not pressed accidentally. */}
          {estimatePrepared && isOverThreshold && !hasDepositInvoice && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  disabled={loading}
                >
                  {loadingAction === 'send_deposit_invoice' ? 'Sending…' : 'Send Deposit Invoice (manual)'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Send deposit invoice manually?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Deposit invoices normally go out automatically when the client accepts
                    the estimate. Use this only after verbal acceptance or if the automatic
                    send failed. Stripe will email a 50% deposit invoice immediately.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={sendDepositInvoice} disabled={loading}>
                    {loadingAction === 'send_deposit_invoice' ? 'Sending…' : 'Send Now'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Step 3: Mark deposit paid */}
          {/* Step 3: Send final invoice (delivered only) — snapshots current line items */}
          {isDelivered &&
            !hasFinalInvoice && (
              <>
                <p className="text-xs text-muted-foreground">
                  Final invoice will use the engagement's current line items. Adjust qty (e.g. for
                  overages) before sending — once sent, the invoice is locked.
                </p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      disabled={loading}
                    >
                      {loadingAction === 'send_final_invoice' ? 'Sending…' : 'Review & Send Final Invoice'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Send the final Stripe invoice?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Stripe will email the invoice to {engagement.clientEmail} immediately.
                        Confirm that the saved line items include all approved changes and overages.
                        Once sent, this invoice is locked in the site.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Go Back</AlertDialogCancel>
                      <AlertDialogAction onClick={sendFinalInvoice} disabled={loading}>
                        {loadingAction === 'send_final_invoice' ? 'Sending…' : 'Send Final Invoice'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
        </div>

      </div>
    </div>
  )
}
